import { useMemo } from 'react';
import { toDictionary } from '@bbkAdminRedux/redux_utils';
import {
  bbkApiSlice,
  type BBKRequestContextArgs,
} from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { DEFAULT_BRAND_ID } from '@bbkAdminRedux/rtkq/brands.slice';
import { type AppKey } from '@bbkAdminRedux/rtkq/current-user.slice';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';

export const ESSENTIALS_PLAN_MAX_PAGES = 5;
export const ESSENTIALS_PLAN_MAX_PAGES_TEXT = 'five';

export enum FunnelType {
  DEFLECT = 'DEFLECT',
  LIFECYCLE = 'LIFECYCLE',
}

type GetFunnelsArgs = {
  appKey: string;
  funnelType?: FunnelType;
  brandId?: string;
} & BBKRequestContextArgs;

type GetFunnelRoutingMetadataArgs = {
  appKey: string;
  brandId?: string;
} & BBKRequestContextArgs;

type GetLiveFunnelsArgs = {
  appKey: string;
} & BBKRequestContextArgs;

export type RestFunnel = {
  id: number;
  encoded_id: string;
  application_id: number;
  experiment_id: string;
  active: boolean;
  display_name?: string;
  definition: string;
  type?: FunnelType;
  traffic_percent: number;
  created_at: string;
  updated_at: string;
  is_control: boolean;
  is_template: boolean;
  brand_id?: string;
  published_at?: string;
  publish_state?: 'live' | 'draft';
  offer_name_offer_placement_info?: OfferNameOfferPlacementInfo;
  cancelation_request?: 'url' | 'direct' | 'webhook' | 'email';
};

export type OfferNameOfferPlacementInfo = {
  [key: string]: OfferPlacement;
};

export type OfferPlacement = {
  vanity_reasons_list: number[];
  final_offer: boolean;
  first_load_offer: boolean;
  loss_aversion_left: boolean;
  loss_aversion_right: boolean;
};

export type RestExperiment = {
  id: string;
  display_name: string;
  active: boolean;
  master: boolean;
  traffic_percent: number;
  created_at: string;
  funnels: RestFunnel[];
};
export type RestExperiments = {
  experiments: RestExperiment[];
};
type SaveFunnelArgs = {
  appKey: string;
  funnelId: string;
  funnel: RestFunnel;
};

export type CopyFunnelArgs = {
  appKey: string;
  companyKey: string;
  funnelId: number;
  brandId?: string;
};

export type FunnelRoutingMetadata = {
  funnel_key: string;
  is_control: boolean;
  is_fallback: boolean;
  audience_segments: number;
};

export type TargetListingMetadata = {
  target_key: string;
  is_global_control: boolean;
  is_global_fallback: boolean;
  is_targeted: boolean;
  play_placements: string[];
};

//TODO: Look into funnel invalidation and optimisations (by brand for better performance)
export const funnelsSlice = bbkApiSlice.injectEndpoints({
  endpoints: (build) => ({
    getFunnels: build.query<RestFunnel[], GetFunnelsArgs>({
      query: ({ appKey, context, funnelType }) => {
        return {
          url: `/api/v1/apps/${appKey}/experiments`,
          method: 'GET',
          params: {
            context: context,
            funnel_type: funnelType ?? FunnelType.DEFLECT,
          },
        };
      },
      transformResponse: (response: RestExperiments) =>
        response.experiments.filter((e) => e.active).flatMap((e) => e.funnels),
      providesTags: ['Funnels'],
    }),
    getFunnelsWithMetaData: build.query<RestFunnel[], GetFunnelsArgs>({
      query: ({ appKey, context, funnelType, brandId }) => {
        return {
          url: `/api/v1/apps/${appKey}/funnels`,
          method: 'GET',
          params: {
            context: context,
            funnel_type: funnelType ?? FunnelType.DEFLECT,
            brand_id: brandId,
          },
        };
      },
      providesTags: ['Funnels'],
    }),
    getLiteFunnels: build.query<RestFunnel[], GetFunnelsArgs>({
      query: ({ appKey, context, funnelType }) => {
        return {
          url: `/api/v1/apps/${appKey}/funnels/lite`,
          method: 'GET',
          params: {
            context: context,
            funnel_type: funnelType ?? FunnelType.DEFLECT,
          },
        };
      },
      providesTags: ['Funnels'],
    }),
    getFunnelRoutingMetadata: build.query<
      FunnelRoutingMetadata[],
      GetFunnelRoutingMetadataArgs
    >({
      query: (args) => ({
        url: `/api/v1/apps/${args.appKey}/funnel_routing_metadata`,
        method: 'GET',
        params: {
          context: args.context,
          brand_id: args.brandId,
        },
      }),
      providesTags: ['FunnelRoutingMetadata'],
    }),
    getFunnelListingMetadata: build.query<
      TargetListingMetadata[],
      GetFunnelRoutingMetadataArgs
    >({
      query: (args) => ({
        url: `/api/v1/apps/${args.appKey}/funnels/meta`,
        method: 'GET',
        params: {
          context: args.context,
          brand_id: args.brandId,
        },
      }),
      providesTags: ['FunnelListingMetadata'],
    }),
    getLiveFunnels: build.query<RestFunnel[], GetLiveFunnelsArgs>({
      query: (args) => ({
        url: `/api/v1/apps/${args.appKey}/live_funnels`,
        method: 'GET',
        params: {
          context: args.context,
        },
      }),
      //FIXME: LiveFunnels is not invalidated anywhere, is this intentional? Live funnels (name?) will be affected by funnel updates?
      providesTags: ['LiveFunnels'],
    }),
    saveFunnel: build.mutation<RestFunnel, SaveFunnelArgs>({
      query: ({ appKey, funnelId, funnel }) => ({
        method: 'PUT',
        url: `/api/v1/apps/${appKey}/funnel/${funnelId}`,
        body: funnel,
      }),
      invalidatesTags: ['Funnels', 'Reasons'],
    }),
    copyFunnel: build.mutation<RestFunnel, CopyFunnelArgs>({
      query: ({ companyKey, appKey, funnelId, brandId }) => {
        return {
          method: 'POST',
          url: `/api/company/${companyKey}/app/${appKey}/funnel/${funnelId}/copy`,
          params: {
            brand_id: brandId,
          },
        };
      },
      invalidatesTags: ['Funnels', 'Placements'],
    }),
    updateCancelationRequest: build.mutation<
      void,
      { app_key: string; funnel_id: number; cancelation_request: string }
    >({
      query: (args) => ({
        url: `/api/v1/apps/${args.app_key}/funnel/${args.funnel_id}/cancelation_request?request_type=${args.cancelation_request}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Funnels'],
    }),
    createPagesAndTargeting: build.mutation<
      void,
      {
        appKey: AppKey;
        offersIdsPercentages: { offer_id: string; percent: number }[];
      }
    >({
      query: ({ appKey, offersIdsPercentages }) => ({
        url: `/api/v1/apps/${appKey}/funnels_by_offers`,
        method: 'POST',
        body: { offers: offersIdsPercentages },
      }),
      invalidatesTags: [
        'Funnels',
        'Routing',
        'PageAndOfferCounts',
        'Experience',
        'Placements',
        'Offers',
      ],
    }),
  }),
});

const matchBrand = (f: RestFunnel, brandId?: string) =>
  brandId && brandId !== DEFAULT_BRAND_ID
    ? f.brand_id === brandId
    : !f.brand_id;

//central all funnel selector
export const useSelectAllFunnels = (
  args: GetFunnelsArgs,
  skip: boolean = false
) => {
  const isMultiBrandFunnelAPIEnabled = useFeatureFlags(
    FeatureFlagsE.SERVER_SIDE_BRAND_FILTER
  );

  const { data: allFunnelsWithMetadata } =
    funnelsSlice.endpoints.getFunnels.useQuery(args, {
      skip: !args.appKey || isMultiBrandFunnelAPIEnabled || skip,
    });

  const { data: liteFunnels } = funnelsSlice.endpoints.getLiteFunnels.useQuery(
    args,
    {
      skip: !args.appKey || !isMultiBrandFunnelAPIEnabled || skip,
    }
  );

  const funnels = isMultiBrandFunnelAPIEnabled
    ? liteFunnels
    : allFunnelsWithMetadata;
  return useMemo(() => funnels ?? [], [funnels]);
};

//filtering by brand and also return all funnels when needed, needed specifically for reports filter picker
export const useSelectFunnelsByBrandIdOrAll = (
  funnelsArgs: GetFunnelsArgs,
  skip: boolean = false
) => {
  const { brandId, ...restArgs } = funnelsArgs;
  const allFunnels = useSelectAllFunnels(restArgs, skip);
  return useMemo(
    () =>
      allFunnels.filter(
        (f) =>
          !brandId ||
          (brandId === DEFAULT_BRAND_ID && f.brand_id === undefined) ||
          f.brand_id === brandId
      ),
    [allFunnels, brandId]
  );
};

export const useSelectAllFunnelsEntities = (
  args: GetFunnelsArgs,
  skip: boolean = false
) => {
  const funnels = useSelectAllFunnels(args, skip);
  return useMemo(() => toDictionary(funnels), [funnels]);
};

export const useSelectActiveFunnels = (
  args: GetFunnelsArgs,
  skip: boolean = false
) => {
  const funnels = useSelectAllFunnels(args, skip);
  return useMemo(() => funnels.filter((f) => f.active), [funnels]);
};

//brand specific selectors:
//central brand funnel selector
export const useSelectBrandFunnels = (
  args: GetFunnelsArgs,
  skip: boolean = false
) => {
  const isMultiBrandFunnelAPIEnabled = useFeatureFlags(
    FeatureFlagsE.SERVER_SIDE_BRAND_FILTER
  );
  const { brandId, ...restArgs } = args;

  const { data: brandedFunnelsWithMetaData } =
    funnelsSlice.endpoints.getFunnelsWithMetaData.useQuery(
      {
        brandId: brandId ?? DEFAULT_BRAND_ID,
        ...restArgs,
      },
      {
        skip: !args.appKey || !isMultiBrandFunnelAPIEnabled || skip,
      }
    );

  const { data: allFunnelsWithMetadata } =
    funnelsSlice.endpoints.getFunnels.useQuery(args, {
      skip: !args.appKey || isMultiBrandFunnelAPIEnabled || skip,
    });

  const funnels =
    (isMultiBrandFunnelAPIEnabled
      ? brandedFunnelsWithMetaData
      : allFunnelsWithMetadata) ?? [];
  return useMemo(
    () =>
      args.brandId
        ? funnels.filter((f) => matchBrand(f, args.brandId))
        : funnels,
    [funnels, args.brandId]
  );
};

export const useSelectBrandActiveFunnels = (
  args: GetFunnelsArgs,
  skip: boolean = false
) => {
  const funnels = useSelectBrandFunnels(args, skip);
  return useMemo(
    () => funnels.filter((f) => f.active && matchBrand(f, args.brandId)),
    [funnels, args.brandId]
  );
};

export const useSelectArchivedFunnels = (
  args: GetFunnelsArgs,
  skip: boolean = false
) => {
  const funnels = useSelectBrandFunnels(args, skip);
  return useMemo(
    () => funnels.filter((f) => !f.active && matchBrand(f, args.brandId)),
    [funnels, args.brandId]
  );
};

export const useSelectTemplateFunnel = (
  args: GetFunnelsArgs,
  skip: boolean = false
) => {
  const funnels = useSelectBrandFunnels(args, skip);
  return useMemo(
    () => funnels.find((f) => f.is_template && matchBrand(f, args.brandId)),
    [funnels, args.brandId]
  );
};

export const useSelectControlFunnel = (
  args: GetFunnelsArgs,
  skip: boolean = false
) => {
  const funnels = useSelectBrandFunnels(args, skip);
  return useMemo(
    () => funnels.find((f) => f.is_control && matchBrand(f, args.brandId)),
    [funnels, args.brandId]
  );
};

//Revisit usages and choose brand specific/lite API
export const useSelectFunnelByIdAndBrand = (
  args: GetFunnelsArgs,
  funnelId: number,
  skip: boolean = false
) => {
  const funnels = useSelectBrandFunnels(args, skip);
  return useMemo(
    () => funnels.find((f) => f.id === funnelId),
    [funnels, funnelId]
  );
};

export const useSelectFunnelsLoading = (
  args: GetFunnelsArgs,
  skip: boolean = false
) => {
  const isMultiBrandFunnelAPIEnabled = useFeatureFlags(
    FeatureFlagsE.SERVER_SIDE_BRAND_FILTER
  );
  const { brandId, ...restArgs } = args;

  const { isLoading: allFunnelsLoading, isFetching: allFunnelsFetching } =
    funnelsSlice.endpoints.getFunnels.useQuery(args, {
      skip: isMultiBrandFunnelAPIEnabled || skip,
    });
  const { isLoading: liteFunnelsLoading, isFetching: liteFunnelsFetching } =
    funnelsSlice.endpoints.getLiteFunnels.useQuery(args, {
      skip: !isMultiBrandFunnelAPIEnabled || skip,
    });
  const {
    isLoading: metaDataFunnelsLoading,
    isFetching: metaDataFunnelsFetching,
  } = funnelsSlice.endpoints.getFunnelsWithMetaData.useQuery(
    {
      brandId: brandId ?? DEFAULT_BRAND_ID,
      ...restArgs,
    },
    {
      skip: !isMultiBrandFunnelAPIEnabled || skip,
    }
  );

  return {
    isLoading:
      allFunnelsLoading || liteFunnelsLoading || metaDataFunnelsLoading,
    isFetching:
      allFunnelsFetching || liteFunnelsFetching || metaDataFunnelsFetching,
  };
};
